import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Check,
  ChevronDown,
  FilePlus2,
  Loader2,
  PencilLine,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  User,
  Users,
  X,
} from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import {
  projectService,
  type Project,
  type ProjectMember,
  type SubmissionSummary,
} from '../../services/projectService';

import './ProjectLanding.css';

type ProjectFilter = 'all' | 'Completed' | 'Processing' | 'Failed';

interface StatusPresentation {
  label: string;
  className: string;
}

interface ScorePresentation {
  valueLabel: string;
  fillWidth: string;
  fillColor: string;
  valueColor: string;
}

interface SubmissionHistoryState {
  submissions: SubmissionSummary[];
  isLoading: boolean;
  error: string | null;
  hasLoaded: boolean;
}

interface EditProjectFormState {
  title: string;
  description: string;
}

const PAGE_FILTERS: Array<{ key: ProjectFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'Completed', label: 'AI Processed' },
  { key: 'Processing', label: 'AI Processing' },
  { key: 'Failed', label: 'AI Error' },
];

const dateFormatter = new Intl.DateTimeFormat('en-GB', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const memberTonePalette: CSSProperties[] = [
  {
    backgroundColor: 'rgba(0, 84, 166, 0.12)',
    color: 'var(--color-blue-m1)',
  },
  {
    backgroundColor: 'rgba(167, 34, 68, 0.12)',
    color: 'var(--color-red-ntu)',
  },
  {
    backgroundColor: 'rgba(50, 188, 173, 0.16)',
    color: 'var(--color-blue-ntu)',
  },
  {
    backgroundColor: 'rgba(7, 177, 82, 0.16)',
    color: 'var(--color-green-p1)',
  },
];

const formatDate = (value?: string) => {
  if (!value) {
    return '—';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '—';
  }

  return dateFormatter.format(parsed);
};

const getMemberName = (member: ProjectMember) => member.display_name || member.email;

const getMemberInitials = (member: ProjectMember) => {
  const source = getMemberName(member).trim();
  if (!source) {
    return 'NA';
  }

  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const getMemberTone = (member: ProjectMember, index: number): CSSProperties => {
  if (member.role === 'owner') {
    return {
      backgroundColor: 'rgba(24, 28, 98, 0.12)',
      color: 'var(--color-blue-ntu)',
    };
  }

  return memberTonePalette[index % memberTonePalette.length];
};

const getProjectTypeLabel = (projectType?: Project['project_type']) =>
  projectType === 'group' ? 'Group' : 'Individual';

const getProjectStatusPresentation = (status: Project['status']): StatusPresentation => {
  if (status === 'Completed') {
    return { label: 'AI Processed', className: 'is-completed' };
  }

  if (status === 'Processing') {
    return { label: 'AI Processing', className: 'is-processing' };
  }

  return { label: 'AI Error', className: 'is-failed' };
};

const getSubmissionStatusPresentation = (status: string): StatusPresentation => {
  const normalizedStatus = status.toLowerCase();

  if (normalizedStatus === 'completed') {
    return { label: 'AI Processed', className: 'is-completed' };
  }

  if (normalizedStatus === 'processing' || normalizedStatus === 'pending') {
    return { label: 'AI Processing', className: 'is-processing' };
  }

  if (normalizedStatus === 'failed') {
    return { label: 'AI Error', className: 'is-failed' };
  }

  return { label: status, className: 'is-muted' };
};

const parseScoreValue = (
  score: number | string | null | undefined,
): number | undefined => {
  if (score === null || score === undefined) {
    return undefined;
  }

  if (typeof score === 'number') {
    return Number.isFinite(score) ? score : undefined;
  }

  if (score.trim() === '') {
    return undefined;
  }

  const parsedValue = Number(score);
  return Number.isFinite(parsedValue) ? parsedValue : undefined;
};

const getScorePresentation = (
  score: number | string | null | undefined,
  status?: Project['status'],
): ScorePresentation => {
  const normalizedScore = parseScoreValue(score);

  if (status === 'Processing') {
    return {
      valueLabel: 'Pending',
      fillWidth: '33%',
      fillColor: 'var(--color-blue-m2)',
      valueColor: 'var(--color-blue-m1)',
    };
  }

  if (status === 'Failed' || normalizedScore === undefined) {
    return {
      valueLabel: '—',
      fillWidth: '0%',
      fillColor: 'var(--color-grey-25)',
      valueColor: 'var(--color-grey-55)',
    };
  }

  if (normalizedScore >= 2.2) {
    return {
      valueLabel: normalizedScore.toFixed(1),
      fillWidth: `${Math.min(100, (normalizedScore / 3) * 100)}%`,
      fillColor: 'var(--color-green)',
      valueColor: 'var(--color-green-p1)',
    };
  }

  if (normalizedScore >= 1.5) {
    return {
      valueLabel: normalizedScore.toFixed(1),
      fillWidth: `${Math.min(100, (normalizedScore / 3) * 100)}%`,
      fillColor: '#b8860b',
      valueColor: '#8a6704',
    };
  }

  return {
    valueLabel: normalizedScore.toFixed(1),
    fillWidth: `${Math.min(100, (normalizedScore / 3) * 100)}%`,
    fillColor: 'var(--color-red-ntu)',
    valueColor: 'var(--color-red-ntu)',
  };
};

const ProjectStatusBadge = ({
  error,
  status,
}: {
  error?: string;
  status: Project['status'];
}) => {
  const presentation = getProjectStatusPresentation(status);

  return (
    <span
      className={`project-landing__status-badge ${presentation.className}`}
      title={status === 'Failed' ? error || 'AI evaluation failed' : undefined}
    >
      {status === 'Completed' && <Check size={14} strokeWidth={2.5} />}
      {status === 'Processing' && <Loader2 size={14} className="spin" strokeWidth={2.5} />}
      {status === 'Failed' && <AlertCircle size={14} strokeWidth={2.5} />}
      <span>{presentation.label}</span>
    </span>
  );
};

const SubmissionStatusBadge = ({ status }: { status: string }) => {
  const presentation = getSubmissionStatusPresentation(status);

  return (
    <span
      className={`project-landing__status-badge ${presentation.className} project-landing__status-badge--compact`}
    >
      {presentation.className === 'is-completed' && <Check size={13} strokeWidth={2.5} />}
      {presentation.className === 'is-processing' && (
        <Loader2 size={13} className="spin" strokeWidth={2.5} />
      )}
      {presentation.className === 'is-failed' && <AlertCircle size={13} strokeWidth={2.5} />}
      <span>{presentation.label}</span>
    </span>
  );
};

const getSubmissionLabel = (submission: SubmissionSummary) => {
  if (submission.name.trim()) {
    return submission.name;
  }

  if (submission.iteration_number <= 1) {
    return submission.iteration_number === 1 ? 'Draft' : 'Submission';
  }

  return `Submission ${submission.iteration_number - 1}`;
};

const getReportUrl = (projectId: string, submissionId?: string) => {
  const params = submissionId ? `?submissionId=${encodeURIComponent(submissionId)}` : '';
  return `/report/${projectId}${params}`;
};

const getResubmitUrl = (projectId: string) => `/report/${projectId}?resubmit=1`;

const MemberModal = ({
  project,
  onClose,
}: {
  project: Project;
  onClose: () => void;
}) => (
  <>
    <button
      type="button"
      className="project-landing__modal-overlay"
      onClick={onClose}
      aria-label="Close member details"
    />
    <div className="project-landing__modal" role="dialog" aria-modal="true">
      <div className="project-landing__modal-header">
        <div>
          <h2 className="project-landing__modal-title">
            {project.members.length > 1
              ? `Team Members (${project.members.length})`
              : 'Member Info'}
          </h2>
          <p className="project-landing__modal-subtitle">{project.title}</p>
        </div>
        <button
          type="button"
          className="project-landing__icon-button"
          onClick={onClose}
          aria-label="Close member details"
        >
          <X size={18} strokeWidth={2.2} />
        </button>
      </div>

      <div className="project-landing__modal-body">
        {project.members.map((member, index) => (
          <div key={member.user_id} className="project-landing__member-card">
            <div
              className="project-landing__member-avatar"
              style={getMemberTone(member, index)}
            >
              {getMemberInitials(member)}
            </div>

            <div className="project-landing__member-content">
              <div className="project-landing__member-heading">
                <div>
                  <div className="project-landing__member-name">{getMemberName(member)}</div>
                  <div className="project-landing__member-role">
                    {member.role === 'owner' ? 'Project owner' : 'Team member'}
                  </div>
                </div>
              </div>

              <div className="project-landing__member-meta">
                <div>
                  <span className="project-landing__member-label">Email</span>
                  <span className="project-landing__member-value">{member.email}</span>
                </div>
                <div>
                  <span className="project-landing__member-label">Project Role</span>
                  <span className="project-landing__member-value">{member.role}</span>
                </div>
                <div>
                  <span className="project-landing__member-label">Project Type</span>
                  <span className="project-landing__member-value">
                    {getProjectTypeLabel(project.project_type)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </>
);

const EditProjectModal = ({
  form,
  isSaving,
  project,
  updateForm,
  onClose,
  onSave,
}: {
  form: EditProjectFormState;
  isSaving: boolean;
  project: Project;
  updateForm: (form: EditProjectFormState) => void;
  onClose: () => void;
  onSave: () => void;
}) => {
  const hasSubmissions = Boolean(project.latest_submission_id);

  return (
    <>
      <button
        type="button"
        className="project-landing__modal-overlay"
        onClick={onClose}
        aria-label="Close edit project"
      />
      <div className="project-landing__modal" role="dialog" aria-modal="true">
        <div className="project-landing__modal-header">
          <div>
            <h2 className="project-landing__modal-title">Edit Project</h2>
            <p className="project-landing__modal-subtitle">{project.title}</p>
          </div>
          <button
            type="button"
            className="project-landing__icon-button"
            onClick={onClose}
            aria-label="Close edit project"
          >
            <X size={18} strokeWidth={2.2} />
          </button>
        </div>

        <div className="project-landing__modal-body">
          <label className="project-landing__edit-field">
            <span>Project Name</span>
            <input
              value={form.title}
              maxLength={120}
              onChange={(event) =>
                updateForm({
                  ...form,
                  title: event.target.value,
                })
              }
            />
          </label>

          <label className="project-landing__edit-field">
            <span>Description</span>
            <textarea
              value={form.description}
              maxLength={300}
              rows={4}
              onChange={(event) =>
                updateForm({
                  ...form,
                  description: event.target.value,
                })
              }
            />
            <small>{form.description.length}/300</small>
          </label>

          <div className="project-landing__edit-readonly-grid">
            <div>
              <span>Course</span>
              <strong>{project.course_code || 'Unassigned'}</strong>
            </div>
            <div>
              <span>Project Type</span>
              <strong>{getProjectTypeLabel(project.project_type)}</strong>
            </div>
          </div>

          {hasSubmissions && (
            <div className="project-landing__edit-info">
              <AlertCircle size={16} strokeWidth={2.4} />
              <p>
                Project details can be updated here. To revise the submitted work,
                use <strong>Submit New Version</strong> in the submission history.
              </p>
            </div>
          )}
        </div>

        <div className="project-landing__modal-footer">
          <button
            type="button"
            className="project-landing__small-button"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="project-landing__small-button is-primary"
            onClick={onSave}
            disabled={isSaving || !form.title.trim()}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </>
  );
};

const ProjectLanding = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<ProjectFilter>('all');
  const [expandedProjectId, setExpandedProjectId] = useState<string | null>(null);
  const [memberModalProject, setMemberModalProject] = useState<Project | null>(null);
  const [editModalProject, setEditModalProject] = useState<Project | null>(null);
  const [editForm, setEditForm] = useState<EditProjectFormState>({
    title: '',
    description: '',
  });
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [retryingProjectId, setRetryingProjectId] = useState<string | null>(null);
  const [submissionHistoryByProject, setSubmissionHistoryByProject] = useState<
    Record<string, SubmissionHistoryState>
  >({});

  const hasProcessingProjects = useMemo(
    () => projects.some((project) => project.status === 'Processing'),
    [projects],
  );

  const loadProjects = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setIsLoading(true);
      }

      setError(null);
      const data = await projectService.getProjects();
      setProjects(data);
    } catch (loadError) {
      console.error('Failed to load projects:', loadError);
      setError('Failed to load projects. Please try again.');
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  }, []);

  const ensureSubmissionHistory = useCallback(
    async (projectId: string) => {
      const currentState = submissionHistoryByProject[projectId];
      if (currentState?.hasLoaded || currentState?.isLoading) {
        return;
      }

      setSubmissionHistoryByProject((previousState) => ({
        ...previousState,
        [projectId]: {
          submissions: [],
          isLoading: true,
          error: null,
          hasLoaded: false,
        },
      }));

      try {
        const response = await projectService.getProjectSubmissions(projectId);
        setSubmissionHistoryByProject((previousState) => ({
          ...previousState,
          [projectId]: {
            submissions: response.submissions,
            isLoading: false,
            error: null,
            hasLoaded: true,
          },
        }));
      } catch (submissionError) {
        console.error('Failed to load project submissions:', submissionError);
        setSubmissionHistoryByProject((previousState) => ({
          ...previousState,
          [projectId]: {
            submissions: [],
            isLoading: false,
            error: 'Failed to load submission history.',
            hasLoaded: true,
          },
        }));
      }
    },
    [submissionHistoryByProject],
  );

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    if (!hasProcessingProjects) {
      return undefined;
    }

    const pollInterval = window.setInterval(() => {
      loadProjects(false);
    }, 5000);

    return () => window.clearInterval(pollInterval);
  }, [hasProcessingProjects, loadProjects]);

  useEffect(() => {
    if (!memberModalProject && !editModalProject) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMemberModalProject(null);
        setEditModalProject(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editModalProject, memberModalProject]);

  const searchedProjects = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return projects;
    }

    return projects.filter((project) => {
      if (project.course_code?.toLowerCase().includes(normalizedQuery)) {
        return true;
      }

      if (project.title.toLowerCase().includes(normalizedQuery)) {
        return true;
      }

      if (project.status.toLowerCase().includes(normalizedQuery)) {
        return true;
      }

      return project.members.some((member) => {
        const memberName = getMemberName(member).toLowerCase();
        return (
          memberName.includes(normalizedQuery) ||
          member.email.toLowerCase().includes(normalizedQuery)
        );
      });
    });
  }, [projects, query]);

  const filterCounts = useMemo(() => {
    const counts: Record<ProjectFilter, number> = {
      all: searchedProjects.length,
      Completed: 0,
      Processing: 0,
      Failed: 0,
    };

    searchedProjects.forEach((project) => {
      counts[project.status] += 1;
    });

    return counts;
  }, [searchedProjects]);

  const filteredProjects = useMemo(() => {
    if (activeFilter === 'all') {
      return searchedProjects;
    }

    return searchedProjects.filter((project) => project.status === activeFilter);
  }, [activeFilter, searchedProjects]);

  useEffect(() => {
    if (
      expandedProjectId &&
      !filteredProjects.some((project) => project.id === expandedProjectId)
    ) {
      setExpandedProjectId(null);
    }
  }, [expandedProjectId, filteredProjects]);

  const handleToggleExpand = (projectId: string) => {
    if (expandedProjectId === projectId) {
      setExpandedProjectId(null);
      return;
    }

    setExpandedProjectId(projectId);
    void ensureSubmissionHistory(projectId);
  };

  const handleOpenEditModal = (project: Project) => {
    setEditModalProject(project);
    setEditForm({
      title: project.title,
      description: project.description || '',
    });
  };

  const handleSaveEditProject = async () => {
    if (!editModalProject) {
      return;
    }

    try {
      setIsSavingEdit(true);
      const updatedProject = await projectService.updateProject(editModalProject.id, {
        title: editForm.title.trim(),
        description: editForm.description.trim(),
      });
      setProjects((previousProjects) =>
        previousProjects.map((project) =>
          project.id === updatedProject.id ? updatedProject : project,
        ),
      );
      setEditModalProject(null);
    } catch (saveError) {
      alert(saveError instanceof Error ? saveError.message : 'Failed to update project.');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleRetryProject = async (project: Project) => {
    try {
      setRetryingProjectId(project.id);
      await projectService.retryProjectEvaluation(project.id);
      setSubmissionHistoryByProject((previousState) => {
        const nextState = { ...previousState };
        delete nextState[project.id];
        return nextState;
      });
      await loadProjects(false);
      await ensureSubmissionHistory(project.id);
    } catch (retryError) {
      alert(retryError instanceof Error ? retryError.message : 'Failed to retry evaluation');
    } finally {
      setRetryingProjectId(null);
    }
  };

  const handleDeleteProject = async (project: Project) => {
    const confirmed = window.confirm(
      `Delete "${project.title}"?\n\nThis will permanently remove this project and all its submissions, evaluations, and feedback. This cannot be undone.`,
    );

    if (!confirmed) {
      return;
    }

    try {
      await projectService.deleteProject(project.id);
      setSubmissionHistoryByProject((previousState) => {
        const nextState = { ...previousState };
        delete nextState[project.id];
        return nextState;
      });
      if (expandedProjectId === project.id) {
        setExpandedProjectId(null);
      }
      await loadProjects(false);
    } catch (deleteError) {
      alert(deleteError instanceof Error ? deleteError.message : 'Failed to delete project');
    }
  };

  if (isLoading) {
    return (
      <section className="project-landing project-landing--status">
        <div className="project-landing__status-panel">
          <Loader2 className="spin" size={20} strokeWidth={2.4} />
          <p className="subtitle-2">Loading projects...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="project-landing project-landing--status">
        <div className="project-landing__status-panel project-landing__status-panel--error">
          <AlertCircle size={20} strokeWidth={2.4} />
          <p className="subtitle-2">{error}</p>
          <button
            type="button"
            className="project-landing__secondary-button"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="project-landing">
        <div className="project-landing__shell">
          <header className="project-landing__header">
            <div>
              <p className="project-landing__eyebrow">Projects Workspace</p>
              <h1 className="project-landing__title heading-5">Projects</h1>
              <p className="project-landing__subtitle">
                Track submissions, monitor AI evaluation progress, and open the
                report workspace for feedback and resubmission.
              </p>
            </div>
          </header>

          <div className="project-landing__toolbar-card">
            <div className="project-landing__toolbar">
              <label className="project-landing__search" aria-label="Search projects">
                <Search size={18} strokeWidth={2.3} />
                <input
                  type="text"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search by course, project name, member, or status..."
                />
              </label>

              <button
                type="button"
                className="project-landing__primary-button"
                onClick={() => navigate('/project/new')}
              >
                <Plus size={16} strokeWidth={2.6} />
                <span>New Project</span>
              </button>
            </div>

            <div className="project-landing__filters">
              <div className="project-landing__filter-list" role="tablist" aria-label="Project filters">
                {PAGE_FILTERS.map((filter) => (
                  <button
                    key={filter.key}
                    type="button"
                    className={`project-landing__filter-chip ${
                      activeFilter === filter.key ? 'is-active' : ''
                    }`}
                    onClick={() => setActiveFilter(filter.key)}
                  >
                    <span>{filter.label}</span>
                    <span className="project-landing__filter-count">
                      {filterCounts[filter.key]}
                    </span>
                  </button>
                ))}
              </div>

              <p className="project-landing__filter-summary">
                {filteredProjects.length === 1
                  ? '1 project matches your current view'
                  : `${filteredProjects.length} projects match your current view`}
              </p>
            </div>
          </div>

          {hasProcessingProjects && (
            <div className="project-landing__banner">
              <Loader2 className="spin" size={16} strokeWidth={2.4} />
              <span>
                Some projects are still being evaluated by AI. This page refreshes every 5
                seconds while processing continues.
              </span>
            </div>
          )}

          {filteredProjects.length === 0 ? (
            <div className="project-landing__empty-state">
              <div className="project-landing__empty-icon">
                <Search size={30} strokeWidth={2.2} />
              </div>
              <h2 className="subtitle-2">No projects found.</h2>
              <p className="project-landing__empty-copy">
                Try a different search or create a new project to get started.
              </p>
              <button
                type="button"
                className="project-landing__primary-button"
                onClick={() => navigate('/project/new')}
              >
                <Plus size={16} strokeWidth={2.6} />
                <span>Create Project</span>
              </button>
            </div>
          ) : (
            <div className="project-landing__table-wrap">
              <div className="project-landing__table-head">
                <span>Project</span>
                <span>Course</span>
                <span>Type</span>
                <span>Date</span>
                <span>Members</span>
                <span>InterQ Score</span>
                <span>Status</span>
                <span>Actions</span>
              </div>

              <div className="project-landing__table-body">
                {filteredProjects.map((project) => {
                  const isExpanded = expandedProjectId === project.id;
                  const scorePresentation = getScorePresentation(
                    project.interq_score,
                    project.status,
                  );
                  const submissionHistory = submissionHistoryByProject[project.id];
                  const canDeleteProject =
                    project.project_type !== 'group' && project.owner_id === user?.id;

                  return (
                    <article
                      key={project.id}
                      className={`project-landing__row-card ${isExpanded ? 'is-expanded' : ''}`}
                    >
                      <div
                        className="project-landing__row"
                        role="button"
                        tabIndex={0}
                        onClick={() => handleToggleExpand(project.id)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleToggleExpand(project.id);
                          }
                        }}
                      >
                        <div className="project-landing__project-cell" data-label="Project">
                          <span className="project-landing__chevron">
                            <ChevronDown size={18} strokeWidth={2.4} />
                          </span>

                          <div className="project-landing__project-copy">
                            <span className="project-landing__project-name">{project.title}</span>
                            <span className="project-landing__project-id">ID: {project.id}</span>
                          </div>
                        </div>

                        <div className="project-landing__cell" data-label="Course">
                          {project.course_code || 'Unassigned'}
                        </div>

                        <div className="project-landing__cell" data-label="Type">
                          <span
                            className={`project-landing__type-badge ${
                              project.project_type === 'group' ? 'is-group' : 'is-individual'
                            }`}
                          >
                            {project.project_type === 'group' ? (
                              <Users size={12} strokeWidth={2.4} />
                            ) : (
                              <User size={12} strokeWidth={2.4} />
                            )}
                            <span>{getProjectTypeLabel(project.project_type)}</span>
                          </span>
                        </div>

                        <div className="project-landing__cell" data-label="Date">
                          {formatDate(project.submission_date || project.created_at)}
                        </div>

                        <div className="project-landing__cell" data-label="Members">
                          <button
                            type="button"
                            className="project-landing__member-trigger"
                            onClick={(event) => {
                              event.stopPropagation();
                              setMemberModalProject(project);
                            }}
                          >
                            <div className="project-landing__avatar-stack" aria-hidden="true">
                              {project.members.slice(0, 3).map((member, index) => (
                                <span
                                  key={member.user_id}
                                  className="project-landing__avatar"
                                  style={getMemberTone(member, index)}
                                >
                                  {getMemberInitials(member)}
                                </span>
                              ))}
                              {project.members.length === 0 && (
                                <span
                                  className="project-landing__avatar"
                                  style={{
                                    backgroundColor: 'rgba(0, 84, 166, 0.12)',
                                    color: 'var(--color-blue-m1)',
                                  }}
                                >
                                  Y
                                </span>
                              )}
                            </div>

                            <span className="project-landing__member-summary">
                              {project.members.length <= 1
                                ? 'Just you'
                                : `${project.members.length} members`}
                            </span>
                          </button>
                        </div>

                        <div className="project-landing__cell" data-label="InterQ Score">
                          <div className="project-landing__score">
                            <div className="project-landing__score-bar">
                              <div
                                className="project-landing__score-fill"
                                style={{
                                  width: scorePresentation.fillWidth,
                                  backgroundColor: scorePresentation.fillColor,
                                }}
                              />
                            </div>
                            <span
                              className="project-landing__score-value"
                              style={{ color: scorePresentation.valueColor }}
                            >
                              {scorePresentation.valueLabel}
                            </span>
                          </div>
                        </div>

                        <div className="project-landing__cell" data-label="Status">
                          <ProjectStatusBadge
                            status={project.status}
                            error={project.ai_processing_error}
                          />
                        </div>

                        <div className="project-landing__cell" data-label="Actions">
                          <div className="project-landing__actions">
                            <button
                              type="button"
                              className="project-landing__icon-button"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleOpenEditModal(project);
                              }}
                              title="Edit project details"
                            >
                              <PencilLine size={16} strokeWidth={2.4} />
                            </button>

                            {project.status === 'Failed' && (
                              <button
                                type="button"
                                className="project-landing__icon-button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  void handleRetryProject(project);
                                }}
                                title="Retry evaluation"
                                disabled={retryingProjectId === project.id}
                              >
                                <RefreshCw
                                  size={16}
                                  strokeWidth={2.4}
                                  className={retryingProjectId === project.id ? 'spin' : undefined}
                                />
                              </button>
                            )}

                            {canDeleteProject && (
                              <button
                                type="button"
                                className="project-landing__icon-button is-danger"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  void handleDeleteProject(project);
                                }}
                                title="Delete project"
                              >
                                <Trash2 size={16} strokeWidth={2.4} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {isExpanded && (
                        <section className="project-landing__expand-panel">
                          <h3 className="project-landing__expand-title">
                            Submission History ({submissionHistory?.submissions.length ?? 0})
                          </h3>

                          {submissionHistory?.isLoading && (
                            <div className="project-landing__expand-status">
                              <Loader2 className="spin" size={16} strokeWidth={2.4} />
                              <span>Loading submission history...</span>
                            </div>
                          )}

                          {submissionHistory?.error && (
                            <div className="project-landing__expand-status is-error">
                              <AlertCircle size={16} strokeWidth={2.4} />
                              <span>{submissionHistory.error}</span>
                              <button
                                type="button"
                                className="project-landing__inline-link"
                                onClick={() => {
                                  setSubmissionHistoryByProject((previousState) => {
                                    const nextState = { ...previousState };
                                    delete nextState[project.id];
                                    return nextState;
                                  });
                                  void ensureSubmissionHistory(project.id);
                                }}
                              >
                                Retry
                              </button>
                            </div>
                          )}

                          {submissionHistory?.hasLoaded &&
                            !submissionHistory.isLoading &&
                            !submissionHistory.error &&
                            submissionHistory.submissions.length === 0 && (
                              <div className="project-landing__expand-status">
                                <span>No submissions recorded yet.</span>
                              </div>
                            )}

                          {submissionHistory?.submissions.length ? (
                            <div className="project-landing__submission-table">
                              <div className="project-landing__submission-head">
                                <span>Version · Date</span>
                                <span>Interdisciplinarity Score</span>
                                <span>Status</span>
                                <span>Actions</span>
                              </div>

                              <div className="project-landing__submission-timeline">
                                {submissionHistory.submissions.map((submission) => {
                                  const submissionScore = getScorePresentation(
                                    submission.avg_score,
                                  );

                                  return (
                                    <div
                                      key={submission.id}
                                      className="project-landing__submission-row"
                                    >
                                      <div
                                        className="project-landing__submission-version"
                                        data-label="Version"
                                      >
                                        <span className="project-landing__timeline-dot" />
                                        <div>
                                          <strong>{getSubmissionLabel(submission)}</strong>
                                          <span>{formatDate(submission.submitted_at)}</span>
                                        </div>
                                      </div>

                                      <div
                                        className="project-landing__submission-score"
                                        data-label="Interdisciplinarity Score"
                                      >
                                        <div className="project-landing__score is-compact">
                                          <div className="project-landing__score-bar">
                                            <div
                                              className="project-landing__score-fill"
                                              style={{
                                                width: submissionScore.fillWidth,
                                                backgroundColor: submissionScore.fillColor,
                                              }}
                                            />
                                          </div>
                                          <span
                                            className="project-landing__score-value"
                                            style={{ color: submissionScore.valueColor }}
                                          >
                                            {submissionScore.valueLabel}
                                          </span>
                                        </div>
                                      </div>

                                      <div data-label="Status">
                                        <SubmissionStatusBadge
                                          status={submission.ai_processing_status}
                                        />
                                      </div>

                                      <div
                                        className="project-landing__submission-actions"
                                        data-label="Actions"
                                      >
                                        <button
                                          type="button"
                                          className="project-landing__small-button is-primary"
                                          onClick={() =>
                                            navigate(getReportUrl(project.id, submission.id))
                                          }
                                        >
                                          View Report
                                        </button>
                                        <button
                                          type="button"
                                          className="project-landing__small-button"
                                          onClick={() =>
                                            alert('Deleting individual submissions is not available yet.')
                                          }
                                        >
                                          Delete
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>

                              <button
                                type="button"
                                className="project-landing__submit-version"
                                onClick={() => navigate(getResubmitUrl(project.id))}
                              >
                                <FilePlus2 size={16} strokeWidth={2.4} />
                                <span>Submit New Version</span>
                              </button>
                            </div>
                          ) : null}
                        </section>
                      )}
                    </article>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {memberModalProject && (
        <MemberModal
          project={memberModalProject}
          onClose={() => setMemberModalProject(null)}
        />
      )}

      {editModalProject && (
        <EditProjectModal
          project={editModalProject}
          form={editForm}
          isSaving={isSavingEdit}
          updateForm={setEditForm}
          onClose={() => setEditModalProject(null)}
          onSave={() => {
            void handleSaveEditProject();
          }}
        />
      )}
    </>
  );
};

export { ProjectLanding };
export default ProjectLanding;
